import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
  XMarkIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import employeeService from '../services/employeeService';
import branchService from '../services/branchService';

const EmployeeManagementNew = () => {
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]); // Dynamic services from database
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    teamMemberName: '',
    email: '',
    password: '',
    mobileNumber: '',
    role: 'Employee',
    teamType: '',
    serviceSpecialization: []
  });

  useEffect(() => {
    fetchEmployees();
    fetchBranches();
    fetchServices(); // Fetch dynamic services
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await employeeService.getAllEmployees();
      const employeesData = response?.data || [];
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
    setLoading(false);
  };

  const fetchBranches = async () => {
    try {
      const response = await branchService.getAllBranches();
      const branchesData = response?.data || [];
      setBranches(Array.isArray(branchesData) ? branchesData : []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchServices = async () => {
    try {
      // Import axios to use proper API base URL
      const api = (await import('../utils/axios')).default;
      const response = await api.get('/service-master');

      if (response.data.success && response.data.services) {
        console.log('✅ Loaded services:', response.data.services.length);
        setServices(response.data.services);
      } else {
        console.log('⚠️ No services found, using defaults');
        setServices([]);
      }
    } catch (error) {
      console.error('❌ Error fetching services:', error);
      setServices([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`📝 Field changed: ${name} = ${value}`);
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      console.log('📋 New formData:', newData);
      return newData;
    });
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get first branch as default if branches exist
    const defaultBranch = branches.length > 0 ? branches[0]._id : null;

    try {
      const employeeData = {
        ...formData,
        branch: defaultBranch, // Auto-assign first branch
        teamMemberEmail: formData.email,
        isActive: true
      };

      if (editingEmployee) {
        await employeeService.updateEmployee(editingEmployee._id, employeeData);
        alert('✅ Employee updated successfully!');
      } else {
        await employeeService.createEmployee(employeeData);
        alert('✅ Employee created successfully!\n\nLogin Credentials:\nEmail: ' + formData.email + '\nPassword: ' + formData.password);
      }

      await fetchEmployees();
      resetForm();
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Failed to save employee. Please try again.');
    }
  };

  const handleEdit = (employee) => {
    console.log('🔍 Editing employee:', employee);
    console.log('📋 Team Type:', employee.teamType);
    console.log('🔧 Service Specialization:', employee.serviceSpecialization);

    setEditingEmployee(employee);
    setFormData({
      teamMemberName: employee.teamMemberName || '',
      email: employee.email || '',
      password: '', // Don't show existing password
      mobileNumber: employee.mobileNumber || '',
      role: employee.role || 'Employee',
      teamType: employee.teamType || '',
      serviceSpecialization: Array.isArray(employee.serviceSpecialization) ? employee.serviceSpecialization : []
    });
    setShowAddModal(true);
  };

  const handleServiceSpecializationChange = (service) => {
    setFormData(prev => {
      const current = Array.isArray(prev.serviceSpecialization) ? prev.serviceSpecialization : [];
      if (current.includes(service)) {
        return { ...prev, serviceSpecialization: current.filter(s => s !== service) };
      } else {
        return { ...prev, serviceSpecialization: [...current, service] };
      }
    });
  };

  const handleDelete = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee? This action cannot be undone.')) {
      try {
        await employeeService.deleteEmployee(employeeId);
        alert('✅ Employee deleted successfully!');
        await fetchEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Failed to delete employee. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      teamMemberName: '',
      email: '',
      password: '',
      mobileNumber: '',
      role: 'Employee',
      teamType: '',
      serviceSpecialization: []
    });
    setEditingEmployee(null);
    setShowPassword(false);
  };

  const getBranchName = (employee) => {
    if (employee.branch?.name) return employee.branch.name;
    if (employee.branch) {
      const branch = branches.find(b => b._id === employee.branch);
      return branch?.name || 'Unknown';
    }
    return 'No Branch';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <UserGroupIcon className="h-6 w-6 mr-2 text-blue-600" />
            Employee Management
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            Add and manage employees with login credentials and branch assignments
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          Add Employee
        </button>
      </div>

      {/* Employees List */}
      {employees.length === 0 ? (
        <div className="text-center py-8">
          <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Employees Found</h3>
          <p className="text-gray-600 mb-4">
            Create your first employee to start managing your team.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Create Employee
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Branch
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Services
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees.map((employee) => (
                <tr key={employee._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">
                          {employee.teamMemberName?.charAt(0).toUpperCase() || 'E'}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {employee.teamMemberName || 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {employee.designation || 'No designation'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{employee.email}</div>
                    <div className="text-sm text-gray-500">{employee.mobileNumber || 'No phone'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getBranchName(employee)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${employee.teamType === 'sales' ? 'bg-indigo-100 text-indigo-800' :
                        employee.teamType === 'reservation' ? 'bg-purple-100 text-purple-800' :
                          employee.teamType === 'other_services' ? 'bg-teal-100 text-teal-800' :
                            employee.teamType === 'accounts' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                      }`}>
                      {employee.teamType ? employee.teamType.replace(/_/g, ' ').toUpperCase() : 'Not Set'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {employee.teamType === 'other_services' && employee.serviceSpecialization && employee.serviceSpecialization.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {employee.serviceSpecialization.slice(0, 3).map((service, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800"
                            title={service.replace(/_/g, ' ')}
                          >
                            {service.replace(/_/g, ' ').substring(0, 10)}
                            {service.length > 10 ? '...' : ''}
                          </span>
                        ))}
                        {employee.serviceSpecialization.length > 3 && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                            +{employee.serviceSpecialization.length - 3}
                          </span>
                        )}
                      </div>
                    ) : employee.teamType === 'other_services' ? (
                      <span className="text-xs text-gray-400 italic">No services assigned</span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                      {employee.role || 'Employee'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${employee.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                      {employee.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(employee)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                      title="Edit Employee"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(employee._id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete Employee"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Name *
                  </label>
                  <input
                    type="text"
                    name="teamMemberName"
                    value={formData.teamMemberName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter full name"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email (Login ID) *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="employee@company.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required={!editingEmployee}
                      placeholder={editingEmployee ? 'Leave blank to keep current' : 'Enter password'}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                      <button
                        type="button"
                        onClick={generatePassword}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Generate Password"
                      >
                        <KeyIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                      >
                        {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mobile Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    placeholder="9876543210"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Employee">Employee</option>
                    <option value="Manager">Manager</option>
                    <option value="Team Lead">Team Lead</option>
                    <option value="Sales Executive">Sales Executive</option>
                  </select>
                </div>

                {/* Team Type */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Type *
                  </label>
                  <select
                    name="teamType"
                    value={formData.teamType}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Team Type</option>
                    <option value="sales">💼 Sales Team</option>
                    <option value="reservation">🎫 Reservation Team</option>
                    <option value="accounts">💰 Accounts Team</option>
                    <option value="other_services">📋 Other Services Team</option>
                    <option value="admin">⚙️ Admin Team</option>
                  </select>
                </div>
              </div>

              {/* Service Specialization - Only for Other Services Team */}
              {formData.teamType === 'other_services' && (
                <div className="col-span-2 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    🔧 Service Specialization * (Select services this employee handles)
                  </label>
                  {services.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {services.map((service) => (
                        <label
                          key={service._id}
                          className={`flex items-center p-2 border rounded cursor-pointer text-sm ${formData.serviceSpecialization?.includes(service.serviceCode)
                              ? 'border-blue-500 bg-blue-100'
                              : 'border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.serviceSpecialization?.includes(service.serviceCode)}
                            onChange={() => handleServiceSpecializationChange(service.serviceCode)}
                            className="mr-2"
                          />
                          <span>{service.icon} {service.serviceName}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-600 mb-2">No services configured yet</p>
                      <p className="text-xs text-gray-500">Please add services from Master Data</p>
                    </div>
                  )}
                  {services.length > 0 && formData.serviceSpecialization?.length === 0 && (
                    <p className="text-xs text-red-600 mt-2">
                      ⚠️ Select at least one service
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingEmployee ? 'Update Employee' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagementNew;
